import { makeAutoObservable, runInAction } from 'mobx'
import axios, { type CancelTokenSource, CanceledError } from 'axios'
import { logger } from 'shared/lib'
import { OutboundSource } from 'entities/OutboundSource/model/OutboundSource'
import { MessagesAnalyticsApi } from 'entities/Analytics'
import { type ISearchDropdownStoreOptions } from 'widgets/SearchDropdown/store/types'

export class SearchDropdownOutboundSourceStore {
  private _allData: OutboundSource[] = []
  private _loading = false
  private _search = ''
  private _maxLimit: number | null = null
  private _disableClosing = false
  private _selectedItemsMapKeys: Map<string, string> = new Map()
  private _options: ISearchDropdownStoreOptions | null = null
  private _cancelTokenSource: CancelTokenSource | null = null

  constructor() {
    makeAutoObservable(this)
  }

  getItemByKey = (key: string) => {
    return this._allData.find((item) => item.key === key)
  }

  initCancelTokenSource = () => {
    this._cancelTokenSource?.cancel()
    this._cancelTokenSource = axios.CancelToken.source()
  }

  initOptions = (options: ISearchDropdownStoreOptions) => {
    this._options = options
  }

  isSelected = (key: string) => {
    return !!this._selectedItemsMapKeys.get(key)
  }

  selectTypeItem = (item: OutboundSource) => {
    this.toggleTypeItem(item)
    this._options?.resetSearch?.()
  }

  resetType = () => {
    this._selectedItemsMapKeys.clear()
    this.reset()
  }

  toggleTypeItem = (item: OutboundSource) => {
    const key = item.key

    if (this._options?.selectedMode) {
      if (this._selectedItemsMapKeys.has(key)) {
        this._selectedItemsMapKeys.delete(key)
      } else {
        this._selectedItemsMapKeys.set(key, key)
      }
    }

    this._options?.toggleTypeItem?.(item)
  }

  handleSearch = (value: string) => {
    this._search = value
  }

  init = async () => {
    try {
      runInAction(() => {
        this._loading = true
        this.initCancelTokenSource()
      })

      const { data } = await MessagesAnalyticsApi.getOutboundSources()

      const allItems = data.map((item) => new OutboundSource(item))

      runInAction(() => {
        this._allData = allItems
      })
    } catch (e) {
      if (e instanceof CanceledError) return

      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  reset = () => {
    this._allData = []
    this._loading = false
    this._search = ''
    this._cancelTokenSource?.cancel()
  }

  setMaxLimit = (maxLimit: number | null) => {
    this._maxLimit = maxLimit
  }

  setDisableClosing = (disableClosing: boolean) => {
    this._disableClosing = disableClosing
  }

  get firstSelectedItemKey() {
    const key = this._selectedItemsMapKeys.entries().next().value?.[0]

    if (!key) return null

    return key
  }

  get selectedItemsKeys() {
    return Array.from(this._selectedItemsMapKeys.values())
  }

  get selectedItemsListKeys() {
    return Array.from(this._selectedItemsMapKeys.entries()).map((item) => item[1])
  }

  get hasSelectedItems() {
    return !!this.selectedItemsCount
  }

  get selectedItemsCount() {
    return this._selectedItemsMapKeys.size
  }

  get filteredItems() {
    const excludeKeys = this.selectedItemsListKeys
    let filtered = this._allData.filter((item) => !excludeKeys.includes(item.key))

    if (this._search.trim()) {
      const searchTerm = this._search.toLowerCase()
      filtered = filtered.filter((item) => item.label.toLowerCase().includes(searchTerm))
    }

    return filtered
  }

  get hasItems() {
    return !!this.filteredItems.length
  }

  get itemsList() {
    return this.filteredItems
  }

  get itemsListKeys() {
    return this.filteredItems.map((item) => item.key)
  }

  get first3Items() {
    return this.filteredItems.slice(0, 3)
  }

  get selectedItemsMapKeys() {
    return this._selectedItemsMapKeys
  }

  get search() {
    return this._search
  }

  get loading() {
    return this._loading
  }

  get options() {
    return this._options
  }

  get disableClosing() {
    return this._disableClosing
  }

  get maxLimit() {
    return this._maxLimit
  }
}
